import type {
  IngestIntake,
  IngestIntakeTabularTarget,
  IngestMappingConfigSummary,
  JobListItem
} from '@axilon-apps/domains-ontology-ui-state'

import { latestActiveVersion, newestFirst } from '../../TabularConfigPage/util/configView'

/**
 * The Configs tab. Links out of the tab hand this to the destination as its
 * "back" target so returning lands here rather than on Runs.
 */
export function configsTabPath(): string {
  return '/ingest?tab=configs'
}

export interface ConfigRow {
  name: string
  /** The highest active version, or the highest archived one when none is active. */
  latest: IngestMappingConfigSummary
  /** Every row of the name, newest first. */
  versions: IngestMappingConfigSummary[]
  /** No active row is left for the name. */
  archived: boolean
  parserKind: 'script' | 'agent'
  tableNames: string[]
}

/** One row per config name, sorted by name. */
export function configRows(all: IngestMappingConfigSummary[]): ConfigRow[] {
  const byName = new Map<string, IngestMappingConfigSummary[]>()
  for (const row of all) {
    const rows = byName.get(row.name)
    if (rows) rows.push(row)
    else byName.set(row.name, [row])
  }
  const result: ConfigRow[] = []
  for (const [name, rows] of byName) {
    const versions = newestFirst(rows)
    const latest = latestActiveVersion(versions) ?? versions[0]
    if (!latest) continue
    result.push({
      name,
      latest,
      versions,
      archived: latest.state !== 'active',
      parserKind: latest.definition.parser.kind,
      tableNames: latest.definition.output.tables.map((table) => table.table)
    })
  }
  return result.sort((a, b) => a.name.localeCompare(b.name))
}

export type BuildCell =
  | { kind: 'none' }
  | { kind: 'building'; jobId: string; mode: 'build' | 'update' }
  | { kind: 'draft'; jobId: string; proposedVersion?: number }

const SETTLED_STATES: ReadonlySet<JobListItem['state']> = new Set(['done', 'error', 'cancelled'])

/**
 * What the newest build of a config is doing. List rows carry no proposed
 * version, so it is derived the way the builder stamps it: 1 for a first
 * build, the base version + 1 for an update.
 */
export function buildCell(name: string, builds: JobListItem[], latestVersion?: number): BuildCell {
  let newest: Extract<JobListItem, { kind: 'mapped_tabular_ingest_build' }> | undefined
  for (const job of builds) {
    if (job.kind !== 'mapped_tabular_ingest_build' || job.payload?.configName !== name) continue
    if (!newest || job.submittedAt > newest.submittedAt) newest = job
  }
  if (!newest || SETTLED_STATES.has(newest.state)) return { kind: 'none' }
  const mode = newest.payload?.mode === 'update' ? 'update' : 'build'
  if (newest.state !== 'awaiting_user') return { kind: 'building', jobId: newest.id, mode }
  const proposedVersion =
    mode === 'build' ? 1 : latestVersion !== undefined ? latestVersion + 1 : undefined
  return {
    kind: 'draft',
    jobId: newest.id,
    ...(proposedVersion !== undefined ? { proposedVersion } : {})
  }
}

export interface UsedByEntry {
  intakeId: string
  intakeName: string
  pinnedVersion?: number
}

/** Intakes whose target runs this config, by intake name. */
export function usedByCell(name: string, intakes: IngestIntake[]): UsedByEntry[] {
  const entries: UsedByEntry[] = []
  for (const intake of intakes) {
    if (intake.targetKind !== 'mapped_tabular_ingest' || !intake.target) continue
    const target = intake.target as IngestIntakeTabularTarget
    if (target.configName !== name) continue
    entries.push({
      intakeId: intake.id,
      intakeName: intake.name,
      ...(target.configVersion ? { pinnedVersion: target.configVersion } : {})
    })
  }
  return entries.sort((a, b) => a.intakeName.localeCompare(b.intakeName))
}

export interface LastRunCell {
  jobId: string
  submittedAt: string
  state: Extract<JobListItem, { kind: 'mapped_tabular_ingest' }>['state']
}

/** The first run of the config in a newest-first list of runs. */
export function lastRunCell(name: string, runs: JobListItem[]): LastRunCell | undefined {
  for (const job of runs) {
    if (job.kind !== 'mapped_tabular_ingest' || job.payload?.config.name !== name) continue
    return { jobId: job.id, submittedAt: job.submittedAt, state: job.state }
  }
  return undefined
}
